import React, { useRef, useState } from 'react';
import { formatTemp, formatTempShort, getWeatherIcon } from '../../utils/weatherUtils';
import Icon from '../../utils/icons';
import './HourlyForecast.css';

function HourlyForecast({ data }) {
  const scrollRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  if (!data?.hours || data.hours.length === 0) {
    return (
      <div className="hourly-forecast">
        <div className="card-header">
          <h2 className="card-title">
            <Icon name="clock" size={18} /> Hourly Forecast
          </h2>
        </div>
        <div className="no-data">No hourly data available</div>
      </div>
    );
  }

  const formatHour = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.round((date - now) / (1000 * 60 * 60));
    
    if (diffHours === 0) return 'Now';
    
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      hour12: true
    });
  };

  const formatFullTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleCardClick = (index) => {
    setSelectedIndex(prev => prev === index ? null : index);
  };

  const hasRain = data.hours.some(h => h.precipitation_chance > 30);
  const selectedHour = selectedIndex !== null ? data.hours[selectedIndex] : null;

  return (
    <div className="hourly-forecast">
      <div className="card-header">
        <div className="title-section">
          <h2 className="card-title">
            <Icon name="clock" size={18} /> Hourly Forecast
          </h2>
          <span className="card-subtitle">Next 24 hours</span>
        </div>
        {hasRain && (
          <div className="rain-alert">
            <Icon name="rain" size={14} />
            <span>Rain expected</span>
          </div>
        )}
        <div className="scroll-controls">
          <button className="scroll-btn" onClick={() => scroll('left')}>
            <Icon name="chevronLeft" size={16} />
          </button>
          <button className="scroll-btn" onClick={() => scroll('right')}>
            <Icon name="chevronRight" size={16} />
          </button>
        </div>
      </div>

      <div className="hourly-scroll" ref={scrollRef}>
        <div className="hourly-container">
          {data.hours.map((hour, index) => {
            const isNow = index === 0;
            const isSelected = selectedIndex === index;
            const precipChance = Math.round(hour.precipitation_chance);
            
            return (
              <div
                key={index}
                className={`hour-card ${isNow ? 'now' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={() => handleCardClick(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCardClick(index); }}
              >
                <span className="hour-time">{formatHour(hour.time)}</span>
                
                <div className="hour-icon-container">
                  <img
                    src={getWeatherIcon(hour.condition?.icon, '2x')}
                    alt={hour.condition?.description || ''}
                    className="hour-icon"
                  />
                </div>
                
                <span className="hour-temp">{formatTempShort(hour.temperature)}</span>
                
                <div className="hour-details">
                  {precipChance > 0 && (
                    <span className="precip-chance">
                      <Icon name="droplet" size={10} /> {precipChance}%
                    </span>
                  )}
                  <span className="hour-wind">
                    <Icon name="wind" size={10} /> {Math.round(hour.wind_speed)}
                  </span>
                </div>

                <div className="temp-bar-container">
                  <div 
                    className="temp-bar"
                    style={{
                      height: `${Math.min(Math.max((hour.temperature - 32) / 68 * 100, 10), 100)}%`
                    }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Expanded detail panel for selected hour */}
      {selectedHour && (
        <div className="hour-detail-panel">
          <div className="hour-detail-header">
            <div className="hour-detail-title">
              <img
                src={getWeatherIcon(selectedHour.condition?.icon, '2x')}
                alt={selectedHour.condition?.description || ''}
                className="hour-detail-icon"
              />
              <div>
                <span className="hour-detail-time">{formatFullTime(selectedHour.time)}</span>
                <span className="hour-detail-condition">
                  {selectedHour.condition?.description
                    ? selectedHour.condition.description.charAt(0).toUpperCase() + selectedHour.condition.description.slice(1)
                    : ''}
                </span>
              </div>
            </div>
            <button
              className="hour-detail-close"
              onClick={() => setSelectedIndex(null)}
              title="Close details"
            >
              ✕
            </button>
          </div>
          <div className="hour-detail-grid">
            <div className="hour-detail-item">
              <Icon name="thermometer" size={14} />
              <span className="hour-detail-label">Temperature</span>
              <span className="hour-detail-value">{formatTemp(selectedHour.temperature)}</span>
            </div>
            <div className="hour-detail-item">
              <Icon name="thermometer" size={14} />
              <span className="hour-detail-label">Feels Like</span>
              <span className="hour-detail-value">{formatTemp(selectedHour.feels_like)}</span>
            </div>
            <div className="hour-detail-item">
              <Icon name="droplet" size={14} />
              <span className="hour-detail-label">Humidity</span>
              <span className="hour-detail-value">{selectedHour.humidity}%</span>
            </div>
            <div className="hour-detail-item">
              <Icon name="wind" size={14} />
              <span className="hour-detail-label">Wind</span>
              <span className="hour-detail-value">{Math.round(selectedHour.wind_speed)} mph</span>
            </div>
            <div className="hour-detail-item">
              <Icon name="cloud" size={14} />
              <span className="hour-detail-label">Cloud Cover</span>
              <span className="hour-detail-value">{selectedHour.clouds}%</span>
            </div>
            <div className="hour-detail-item">
              <Icon name="rain" size={14} />
              <span className="hour-detail-label">Precipitation</span>
              <span className="hour-detail-value">{Math.round(selectedHour.precipitation_chance)}%</span>
            </div>
          </div>
        </div>
      )}

      <div className="hourly-legend">
        <span className="legend-item">
          <span className="legend-dot temp"></span> Temperature
        </span>
        <span className="legend-item">
          <span className="legend-dot precip"></span> Precipitation
        </span>
        {selectedIndex === null && (
          <span className="legend-item legend-hint">
            Click an hour for details
          </span>
        )}
      </div>
    </div>
  );
}

export default HourlyForecast;
